import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { ONEGO_LOGO } from "../lib/api";
import { LogOut, Users, Banknote, Activity, Building2, Shield } from "lucide-react";
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";

function fmt(n) {
    return new Intl.NumberFormat("en-KE", { maximumFractionDigits: 0 }).format(n || 0);
}

export default function AdminConsole() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [overview, setOverview] = useState(null);
    const [users, setUsers] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [providers, setProviders] = useState(null);
    const [tab, setTab] = useState("merchants");
    const [applications, setApplications] = useState([]);

    const loadApps = async () => {
        try {
            const { data } = await api.get("/admin/pickup-applications?status=pending");
            setApplications(data);
        } catch (_) {}
    };

    useEffect(() => {
        (async () => {
            const [ov, us, tx, pr] = await Promise.all([
                api.get("/admin/overview"),
                api.get("/admin/users"),
                api.get("/admin/transactions?limit=50"),
                api.get("/providers/status"),
            ]);
            setOverview(ov.data);
            setUsers(us.data);
            setTransactions(tx.data);
            setProviders(pr.data);
            await loadApps();
        })().catch(() => {});
    }, []);

    const decide = async (userId, decision) => {
        try {
            await api.post(`/admin/pickup-applications/${userId}/decision`, { decision });
            await loadApps();
        } catch (_) {}
    };

    if (user && user.role !== "admin") {
        return (
            <div className="min-h-screen flex items-center justify-center p-6 text-center" data-testid="admin-forbidden">
                <div className="og-card p-10 max-w-md">
                    <Shield size={32} className="mx-auto text-rose-500" />
                    <h1 className="mt-4 font-display text-2xl font-bold text-onego-navy">Admin only</h1>
                    <p className="mt-2 text-slate-600">Sign in with an admin account to access the console.</p>
                    <Link to="/login" className="og-btn-gradient inline-flex mt-5">Sign in</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-onego-bg" data-testid="admin-console">
            <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
                <div className="px-6 lg:px-10 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3">
                        <img src={ONEGO_LOGO} alt="OneGo" className="h-8" />
                        <span className="og-chip" style={{background: "#0B1437", color: "#fff"}}>Admin</span>
                    </Link>
                    <button onClick={async () => { await logout(); navigate("/"); }} className="og-btn-ghost inline-flex items-center gap-2" data-testid="admin-logout">
                        <LogOut size={16} /> Sign out
                    </button>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 lg:px-10 py-8 space-y-8">
                <h1 className="font-display text-4xl font-extrabold text-onego-navy">Platform overview</h1>

                <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                    {[
                        { l: "Merchants", v: overview?.merchants, icon: Users },
                        { l: "Super-agents", v: overview?.super_agents, icon: Users },
                        { l: "Suppliers", v: overview?.suppliers, icon: Building2 },
                        { l: "Network GMV", v: `KES ${fmt(overview?.gmv)}`, icon: Activity, raw: true },
                        { l: "Commission", v: `KES ${fmt(overview?.commission)}`, icon: Banknote, raw: true },
                    ].map((s) => {
                        const Icon = s.icon;
                        return (
                            <div key={s.l} className="og-card p-5">
                                <div className="flex items-center justify-between">
                                    <p className="text-xs uppercase tracking-widest font-semibold text-slate-500">{s.l}</p>
                                    <Icon size={16} className="text-slate-400" />
                                </div>
                                <p className="mt-3 font-display text-2xl font-extrabold text-onego-navy">
                                    {s.raw ? s.v : fmt(s.v)}
                                </p>
                            </div>
                        );
                    })}
                </div>

                <div className="og-card p-7">
                    <h3 className="font-display text-xl font-bold text-onego-navy">Network GMV (last 14 days)</h3>
                    <div className="mt-4 h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={overview?.daily || []}>
                                <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
                                <XAxis dataKey="date" stroke="#94a3b8" tickFormatter={(d) => d.slice(5)} />
                                <YAxis stroke="#94a3b8" />
                                <Tooltip />
                                <Line type="monotone" dataKey="amount" stroke="#22D3EE" strokeWidth={3} dot={{ r: 3, fill: "#22D3EE" }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="og-card p-7">
                    <h3 className="font-display text-xl font-bold text-onego-navy">Providers</h3>
                    <p className="text-sm text-slate-500">Live integration status. Set <code>PROVIDERS_MOCK_MODE=false</code> in backend .env after keys are supplied.</p>
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {providers && Object.entries(providers.providers).map(([k, v]) => (
                            <div key={k} className="border border-slate-100 rounded-2xl p-4 flex items-start justify-between" data-testid={`provider-${k}`}>
                                <div>
                                    <p className="font-bold text-onego-navy capitalize">{k.replace("_", " ")}</p>
                                    <p className="text-xs text-slate-500 mt-1">{v.required_keys.join(", ")}</p>
                                </div>
                                <span className={`og-chip ${v.configured ? "bg-emerald-100 text-emerald-700" : providers.mock_mode ? "bg-cyan-100 text-cyan-700" : "bg-rose-100 text-rose-700"}`}>
                                    {v.configured ? "Live keys" : providers.mock_mode ? "Mock" : "Missing"}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="og-card p-7">
                    <div className="flex items-center gap-2 mb-4">
                        {["merchants", "super_agent", "supplier", "admin"].map((r) => (
                            <button
                                key={r}
                                onClick={() => setTab(r)}
                                className={`px-3 py-1.5 rounded-full text-xs font-bold border transition ${tab === r ? "bg-onego-navy text-white border-onego-navy" : "border-slate-200 text-slate-600"}`}
                                data-testid={`admin-tab-${r}`}
                            >
                                {r === "merchants" ? "Merchant" : r.replace("_", " ")}
                            </button>
                        ))}
                    </div>
                    <table className="w-full text-sm" data-testid="admin-users-table">
                        <thead className="bg-slate-50 text-slate-500 uppercase text-xs tracking-widest">
                            <tr><th className="text-left px-4 py-2">Name</th><th className="text-left px-4 py-2">Email</th><th className="text-left px-4 py-2">Role</th><th className="text-left px-4 py-2">Country</th></tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {users.filter((u) => (tab === "merchants" ? u.role === "merchant" : u.role === tab)).map((u) => (
                                <tr key={u.id}>
                                    <td className="px-4 py-2 font-semibold text-onego-navy">{u.name}</td>
                                    <td className="px-4 py-2 text-slate-600">{u.email}</td>
                                    <td className="px-4 py-2"><span className="og-chip">{u.role}</span></td>
                                    <td className="px-4 py-2 text-slate-600">{u.country}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="og-card p-7" data-testid="pickup-applications">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <h3 className="font-display text-xl font-bold text-onego-navy">Pickup host applications</h3>
                        <span className="og-chip">{applications.length} pending</span>
                    </div>
                    {applications.length === 0 && (
                        <p className="mt-3 text-sm text-slate-500">No pending applications.</p>
                    )}
                    <div className="mt-3 divide-y divide-slate-100">
                        {applications.map((a) => (
                            <div key={a.id} className="py-3 flex items-center justify-between gap-3" data-testid={`pickup-app-${a.id}`}>
                                <div className="min-w-0">
                                    <p className="font-bold text-onego-navy truncate">{a.business_name || a.name}</p>
                                    <p className="text-xs text-slate-500">
                                        {a.email} &middot; {a.address_label || a.country} &middot; capacity {a.pickup_capacity || 50} &middot; {a.pickup_hours || "08:00–18:00"}
                                    </p>
                                </div>
                                <div className="flex gap-2 shrink-0">
                                    <button
                                        onClick={() => decide(a.id, "approved")}
                                        className="og-btn-gradient text-xs px-3 py-1.5"
                                        data-testid={`pickup-approve-${a.id}`}
                                    >
                                        Approve
                                    </button>
                                    <button
                                        onClick={() => decide(a.id, "rejected")}
                                        className="og-btn-ghost text-xs text-rose-600 px-3 py-1.5"
                                        data-testid={`pickup-reject-${a.id}`}
                                    >
                                        Reject
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="og-card p-7">
                    <h3 className="font-display text-xl font-bold text-onego-navy">Recent network transactions</h3>
                    <div className="mt-3 max-h-[300px] overflow-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 text-slate-500 uppercase text-xs tracking-widest sticky top-0">
                                <tr><th className="text-left px-4 py-2">Time</th><th className="text-left px-4 py-2">Type</th><th className="text-left px-4 py-2">Description</th><th className="text-right px-4 py-2">Amount</th></tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {transactions.map((t) => (
                                    <tr key={t.id}>
                                        <td className="px-4 py-2 text-slate-500">{new Date(t.created_at).toLocaleString()}</td>
                                        <td className="px-4 py-2"><span className="og-chip">{t.kind.replace("_", " ")}</span></td>
                                        <td className="px-4 py-2 text-onego-navy font-semibold">{t.meta?.description || "—"}</td>
                                        <td className="px-4 py-2 text-right font-bold">KES {fmt(t.amount)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </main>
        </div>
    );
}
